import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  inject,
  signal,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { forkJoin } from 'rxjs';
import {
  MerchantType,
  ServiceTime,
  Affluence,
} from 'src/app/models/onboarding.model';
import {
  DailyPredictions,
  DashboardKPIs,
  DashboardService,
  WeeklyTrends,
} from 'src/app/services/dashboard.service';
import { DailySalesService } from 'src/app/services/daily-sales.service';
import { AuthService } from 'src/app/services/auth.service';
import { RouterModule } from '@angular/router';
import jsPDF from 'jspdf';
import {
  ACTIVITY_TYPE_LABELS,
  SERVICE_LABELS,
  AFFLUENCE_LABELS,
} from 'src/app/models/onboarding.labels';
import { PlanFeatureService } from 'src/app/services/plan-feature.service';
import { FEATURES } from 'src/app/shared/config/plan-features';
import {
  ClusterService,
  MerchantAlert,
} from 'src/app/services/cluster.service';
import {
  UnifiedAlertService,
  UnifiedAlert,
} from 'src/app/services/unified-alert.service';
import { LogService } from '../../../services/log.service';
import { MenuCardComponent } from 'src/app/shared/components/menu-card/menu-card.component';
import {
  MerchantPeriodService,
  MerchantPeriod,
  PERIOD_TYPE_META,
} from 'src/app/services/merchant-period.service';
import { SpecialPeriodFormComponent } from 'src/app/shared/components/special-period-form/special-period-form.component';
import { AlertPreviewComponent } from 'src/app/shared/components/alert-preview/alert-preview.component';

export type CalendarView = '1w' | '1m' | '3m' | '1y';

export const CALENDAR_VIEW_META: Record<
  CalendarView,
  { label: string; days: number; icon: string }
> = {
  '1w': { label: '7 jours', days: 7, icon: '📅' },
  '1m': { label: '1 mois', days: 30, icon: '📆' },
  '3m': { label: '3 mois', days: 90, icon: '🗓️' },
  '1y': { label: '1 an', days: 365, icon: '📊' },
};

interface DashboardStat {
  label: string;
  value: string | number;
  icon: string;
  trend?: string;
  trendUp?: boolean;
  class?: string;
}

export interface CalendarDay {
  date: Date;

  // Météo
  weather?: {
    icon: string;
    temperature: number;
    temperatureMin?: number;
    temperatureMax?: number;
    condition?: string;
  };

  // Planning
  servicesCount: number;
  /** Noms des événements planifiés ce jour (itinérant) */
  eventNames?: string[];

  // ✅ Tâches
  tasks: number;
  completedTasks: number;

  // Meta
  isWeekend: boolean;
  isToday: boolean;

  // Optionnel
  affluence?: 'low' | 'medium' | 'high';
  alertsCount: number;
  /** Périodes spéciales actives ce jour (triées par priorité) */
  specialPeriods?: { label: string; type: string; color: string | null }[];
  /** @deprecated use specialPeriods */
  specialPeriod?: { label: string; type: string; color: string | null };
}

@Component({
  selector: 'app-dashboard-fixed',
  imports: [
    CommonModule,
    RouterModule,
    MenuCardComponent,
    SpecialPeriodFormComponent,
    AlertPreviewComponent,
  ],
  templateUrl: './dashboard-fixed.component.html',
  styleUrls: ['./dashboard-fixed.component.css'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ height: '0', opacity: 0, overflow: 'hidden' }),
        ),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('slidePanel', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate(
          '300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateX(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateX(100%)' }),
        ),
      ]),
    ]),
  ],
})
export class DashboardFixedComponent implements OnInit {
  private log = inject(LogService);
  // Expose Math pour le template
  Math = Math;
  readonly FEATURES = FEATURES;
  readonly planFeatureService = inject(PlanFeatureService);
  readonly alertService = inject(UnifiedAlertService);
  private cdr = inject(ChangeDetectorRef);
  private merchantPeriodService = inject(MerchantPeriodService);

  // --- Calendrier ---
  readonly CALENDAR_VIEW_META = CALENDAR_VIEW_META;
  readonly calendarViews: CalendarView[] = ['1w', '1m'];
  calendarView = signal<CalendarView>('1w');
  calendarOffset = signal<number>(0);
  merchantPeriods = signal<MerchantPeriod[]>([]);
  readonly PERIOD_TYPE_META = PERIOD_TYPE_META;
  showPeriodForm = signal(false);
  editingPeriod = signal<MerchantPeriod | null>(null);
  /** Jours dont on a ouvert l'expansion des périodes multiples */
  expandedPeriodDays = new Set<string>();

  togglePeriodExpand(day: CalendarDay, event: Event): void {
    event.stopPropagation();
    const key = day.date.toDateString();
    if (this.expandedPeriodDays.has(key)) {
      this.expandedPeriodDays.delete(key);
    } else {
      this.expandedPeriodDays.add(key);
    }
    this.cdr.markForCheck();
  }

  isDayPeriodExpanded(day: CalendarDay): boolean {
    return this.expandedPeriodDays.has(day.date.toDateString());
  }

  // Nom du commerce
  businessName = '';
  showMenuCard = false;
  activityType: string = '';
  userServices: ServiceTime[] = [];
  affluence: Affluence | '' = '';
  userAddress: string = ''; // Adresse du commerce

  // Labels exposés au template
  activityTypeLabels = ACTIVITY_TYPE_LABELS;
  serviceLabels = SERVICE_LABELS;
  affluenceLabels = AFFLUENCE_LABELS;

  weeklyKPIs = {
    precision: 0,
    wasteReduction: 0,
    revenue: 0,
    savings: 0,
  };

  calendarDays: CalendarDay[] = [];
  selectedDay: CalendarDay | null = null;

  tomorrowPredictions: DailyPredictions | null = null;

  weeklyTrends: WeeklyTrends | null = null;
  pendingActions: any[] = [];

  loading = true;

  // Données récupérées via API
  totalEvents = 0;
  avgAccuracy = 0;
  lossAvoided = 0;
  totalRevenue = 0;
  totalSavings = 0;
  stats: DashboardStat[] = [];

  // Stats saisies manuelles
  monthlySalesCount = 0;
  monthlyAverageAccuracy = 0;

  // Accordéon recommandations
  showRecommendations = false;
  showChecklist = false;
  isChecklistClosing = false;
  isChecklistAnimating = false;

  // Tracking de l'expansion de chaque service (par index)
  expandedServices: { [key: number]: boolean } = {};

  // Tracking de l'affichage des détails explicatifs
  showExplanationDetails = false;

  // Tracking de l'affichage détaillé des sections SANS/AVEC
  showDetailedComparison = false;

  // Infos du jour actuel (par défaut)
  todayInfo = {
    dayName: 'Sam',
    dayNumber: 24,
    month: 'Jan',
    weather: { icon: '🌧️', temperature: 14 },
    servicesCount: 2,
    tasks: 4,
    completedTasks: 3,
    isWeekend: true,
    affluence: 'moyenne',
  };

  // Infos du jour sélectionné (utilisé pour l'affichage)
  selectedDayInfo = { ...this.todayInfo };
  selectedDayLabel = 'Recommandations du jour';

  // Tâches de préparation mockées
  preparationTasks = [
    {
      id: 1,
      title: 'Commander pain frais chez le boulanger',
      category: 'Approvisionnement',
      priority: 'high',
      completed: false,
      deadline: '08:00',
      note: 'Stock actuel : 12 unités, besoin estimé : 45',
    },
    {
      id: 2,
      title: 'Vérifier niveau gaz et batterie du camion',
      category: 'Équipement',
      priority: 'high',
      completed: true,
      deadline: '09:00',
    },
    {
      id: 3,
      title: 'Préparer sauce maison (batch pour 2 jours)',
      category: 'Préparation',
      priority: 'medium',
      completed: true,
      deadline: '10:00',
    },
    {
      id: 4,
      title: 'Nettoyer et désinfecter surfaces de travail',
      category: 'Hygiène',
      priority: 'high',
      completed: false,
      deadline: '10:30',
    },
    {
      id: 5,
      title: 'Relancer fournisseur pour livraison viande',
      category: 'Approvisionnement',
      priority: 'high',
      completed: false,
      deadline: '11:00',
      note: '⚠️ Commande passée il y a 2 jours, pas de confirmation',
    },
    {
      id: 6,
      title: 'Contrôler température frigo (-2°C à +4°C)',
      category: 'Hygiène',
      priority: 'medium',
      completed: false,
      deadline: '11:00',
    },
    {
      id: 7,
      title: 'Découper légumes pour service midi',
      category: 'Préparation',
      priority: 'medium',
      completed: false,
      deadline: '11:15',
    },
  ];

  dailyRecommendations = [
    {
      service: 'Service du midi',
      timeSlot: '11h30 - 14h00',
      icon: '🍽️',
      status: 'active',
      products: [
        {
          name: 'Burger Classique',
          icon: '🍔',
          recommended: 45,
          range: '35-55',
          confidence: 92,
        },
        {
          name: 'Burger Bacon',
          icon: '🍔',
          recommended: 38,
          range: '30-46',
          confidence: 90,
        },
        {
          name: 'Burger Végétarien',
          icon: '🥬',
          recommended: 22,
          range: '18-26',
          confidence: 86,
        },
        {
          name: 'Wrap Poulet',
          icon: '🌯',
          recommended: 35,
          range: '28-42',
          confidence: 89,
        },
        {
          name: 'Salade César',
          icon: '🥗',
          recommended: 28,
          range: '22-34',
          confidence: 88,
        },
        {
          name: 'Salade Composée',
          icon: '🥗',
          recommended: 24,
          range: '19-29',
          confidence: 85,
        },
        {
          name: 'Frites',
          icon: '🍟',
          recommended: 60,
          range: '50-70',
          confidence: 90,
        },
        {
          name: 'Onion Rings',
          icon: '🧅',
          recommended: 32,
          range: '26-38',
          confidence: 84,
        },
        {
          name: 'Nuggets',
          icon: '🍗',
          recommended: 40,
          range: '32-48',
          confidence: 87,
        },
        {
          name: 'Coca-Cola',
          icon: '🥤',
          recommended: 55,
          range: '45-65',
          confidence: 85,
        },
        {
          name: 'Sprite',
          icon: '🥤',
          recommended: 42,
          range: '35-50',
          confidence: 83,
        },
        {
          name: 'Eau minérale',
          icon: '💧',
          recommended: 48,
          range: '40-56',
          confidence: 88,
        },
        {
          name: 'Café',
          icon: '☕',
          recommended: 65,
          range: '55-75',
          confidence: 91,
        },
        {
          name: 'Thé glacé',
          icon: '🧋',
          recommended: 28,
          range: '22-34',
          confidence: 82,
        },
        {
          name: 'Milkshake',
          icon: '🥤',
          recommended: 18,
          range: '14-22',
          confidence: 80,
        },
      ],
    },
    {
      service: 'Service du soir',
      timeSlot: '18h30 - 22h00',
      icon: '🌙',
      status: 'active',
      products: [
        {
          name: 'Pizza Margherita',
          icon: '🍕',
          recommended: 32,
          range: '28-38',
          confidence: 89,
        },
        {
          name: 'Pizza 4 Fromages',
          icon: '🍕',
          recommended: 28,
          range: '24-34',
          confidence: 87,
        },
        {
          name: 'Pizza Regina',
          icon: '🍕',
          recommended: 26,
          range: '22-32',
          confidence: 86,
        },
        {
          name: 'Pâtes Carbonara',
          icon: '🍝',
          recommended: 25,
          range: '20-30',
          confidence: 87,
        },
        {
          name: 'Pâtes Bolognaise',
          icon: '🍝',
          recommended: 30,
          range: '25-36',
          confidence: 88,
        },
        {
          name: 'Lasagnes',
          icon: '🍝',
          recommended: 22,
          range: '18-26',
          confidence: 85,
        },
        {
          name: 'Risotto',
          icon: '🍚',
          recommended: 18,
          range: '14-22',
          confidence: 83,
        },
        {
          name: 'Steak Frites',
          icon: '🥩',
          recommended: 24,
          range: '20-28',
          confidence: 86,
        },
        {
          name: 'Poulet Rôti',
          icon: '🍗',
          recommended: 20,
          range: '16-24',
          confidence: 84,
        },
        {
          name: 'Tiramisu',
          icon: '🍰',
          recommended: 18,
          range: '15-22',
          confidence: 84,
        },
        {
          name: 'Panna Cotta',
          icon: '🍮',
          recommended: 15,
          range: '12-18',
          confidence: 82,
        },
        {
          name: 'Tarte Citron',
          icon: '🍋',
          recommended: 12,
          range: '10-16',
          confidence: 80,
        },
        {
          name: 'Vin rouge',
          icon: '🍷',
          recommended: 12,
          range: '10-15',
          confidence: 82,
        },
        {
          name: 'Vin blanc',
          icon: '🍷',
          recommended: 10,
          range: '8-13',
          confidence: 81,
        },
        {
          name: 'Bière pression',
          icon: '🍺',
          recommended: 28,
          range: '24-34',
          confidence: 85,
        },
      ],
    },
  ];

  filteredDailyRecommendations: any[] = [];

  // --- Alertes & Recommandations (données réelles) ---
  merchantAlerts: MerchantAlert[] = [];
  unifiedAlerts: UnifiedAlert[] = [];
  positiveInsights: {
    icon: string;
    title: string;
    message: string;
    color: string;
  }[] = [];

  /** Icône par type d'alerte */
  alertIcon(type: string): string {
    const icons: Record<string, string> = {
      waste: '🗑️',
      rupture: '📦',
      margin: '💸',
      benchmark: '📊',
    };
    return icons[type] || '⚠️';
  }

  /** Couleur de bordure par sévérité */
  alertBorderColor(severity: string): string {
    return severity === 'critical'
      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
      : 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
  }

  constructor(
    private dashboardService: DashboardService,
    private dailySalesService: DailySalesService,
    private authService: AuthService,
    private clusterService: ClusterService,
  ) {
    // Initialiser avec toutes les recommandations par défaut
    this.filteredDailyRecommendations = this.dailyRecommendations;
  }

  ngOnInit() {
    // Pré-remplir le header depuis le cache local (instantané)
    const cached = this.authService.getCurrentUser();
    if (cached) {
      this.businessName = (cached as any).businessName || '';
      this.activityType = ((cached as any).activityType as string) || '';
    }

    this.loadDashboardData();
    this.loadMerchantAlerts();
    this.loadUnifiedAlerts();
    this.loadMerchantPeriods();
    this.generateCalendar();
    this.filterServicesByUserProfile();
  }

  private loadMerchantPeriods(): void {
    const today = new Date();
    const end = new Date(today);
    end.setFullYear(today.getFullYear() + 1);
    this.merchantPeriodService.load(today, end).subscribe({
      next: (res) => {
        this.merchantPeriods.set(res.periods);
        this.generateCalendar(); // Regenerate with period data
        this.cdr.markForCheck();
      },
      error: () => {
        /* periods unavailable, calendar still works */
      },
    });
  }

  setCalendarView(view: CalendarView): void {
    if (
      (view === '3m' || view === '1y') &&
      !this.planFeatureService.canAccess(this.FEATURES.CALENDAR_EXTENDED)
    ) {
      return;
    }
    this.calendarView.set(view);
    this.calendarOffset.set(0);
    this.generateCalendar();
    this.cdr.markForCheck();
  }

  navigateCalendar(dir: -1 | 1): void {
    this.calendarOffset.update((o) => o + dir);
    this.generateCalendar();
    this.cdr.markForCheck();
  }

  resetCalendarToToday(): void {
    this.calendarOffset.set(0);
    this.generateCalendar();
    this.cdr.markForCheck();
  }

  getCalendarRangeLabel(): string {
    const view = this.calendarView();
    const offset = this.calendarOffset();
    const now = new Date();
    const locale = 'fr-FR';
    if (view === '1w') {
      const start = new Date(now);
      start.setDate(now.getDate() + offset * 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (view === '1m') {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    } else if (view === '3m') {
      const start = new Date(now);
      start.setMonth(now.getMonth() + offset * 3);
      const end = new Date(start);
      end.setMonth(start.getMonth() + 3);
      return `${start.toLocaleDateString(locale, { month: 'short', year: 'numeric' })} – ${end.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}`;
    } else {
      const d = new Date(now.getFullYear() + offset, 0, 1);
      return d.getFullYear().toString();
    }
  }

  openPeriodForm(period: MerchantPeriod | null = null): void {
    this.editingPeriod.set(period);
    this.showPeriodForm.set(true);
  }

  closePeriodForm(): void {
    this.showPeriodForm.set(false);
    this.editingPeriod.set(null);
  }

  onPeriodSaved(): void {
    this.closePeriodForm();
    this.merchantPeriods.set(this.merchantPeriodService.periods());
    this.generateCalendar();
    // Refresh selectedDay so the modal shows updated period info
    if (this.selectedDay) {
      const refreshed = this.calendarDays.find((d) =>
        this.isSameDay(d.date, this.selectedDay!.date),
      );
      if (refreshed) this.selectedDay = refreshed;
    }
    this.cdr.markForCheck();
  }

  deletePeriod(period: MerchantPeriod): void {
    this.merchantPeriodService.delete(period._id).subscribe({
      next: () => {
        this.merchantPeriods.set(this.merchantPeriodService.periods());
        this.generateCalendar();
        this.cdr.markForCheck();
      },
    });
  }

  private filterServicesByUserProfile() {
    this.log.debug('🔍 Début du filtrage des services');
    this.authService.getMe().subscribe({
      next: (response) => {
        this.log.debug('📦 Réponse getMe:', response);
        this.log.debug('📦 response.user:', response.user);
        this.log.debug(
          '📦 Propriétés de response.user:',
          Object.keys(response.user),
        );
        this.log.debug('📦 response.user.services:', response.user.services);

        // Récupérer le nom du commerce
        this.businessName = response.user.businessName || 'Commerce';
        this.activityType = (response.user.activityType as string) || '';
        this.affluence = (response.user.affluence as Affluence) || '';

        // Récupérer la localisation (ville — pays)
        const loc = (response.user as any).location;
        if (loc?.city) {
          this.userAddress = loc.country
            ? loc.city + ' — ' + loc.country
            : loc.city;
        }

        if (response.user && response.user.services) {
          const userServices = response.user.services;
          this.userServices = userServices as ServiceTime[];
          this.log.debug('✅ Services utilisateur:', userServices);

          // Mapping des noms de services utilisateur vers les noms dans dailyRecommendations
          const serviceMapping: { [key: string]: string } = {
            Déjeuner: 'Service du midi',
            Dîner: 'Service du soir',
            'Petit-déjeuner': 'Service du matin',
            Brunch: 'Service brunch',
          };

          // Filtrer les recommandations selon les services de l'utilisateur
          this.filteredDailyRecommendations = this.dailyRecommendations.filter(
            (rec) => {
              // Trouver si le service correspond à un des services de l'utilisateur
              return userServices.some((userService: string) => {
                const mappedService = serviceMapping[userService];
                this.log.debug(
                  `  Comparaison: ${userService} -> ${mappedService} vs ${rec.service}`,
                );
                return mappedService === rec.service;
              });
            },
          );

          this.log.debug(
            '✅ Recommandations filtrées:',
            this.filteredDailyRecommendations,
          );
          this.log.debug(
            '✅ Nombre de services filtrés:',
            this.filteredDailyRecommendations.length,
          );
        } else {
          this.log.debug(
            '⚠️ Pas de services dans le profil, affichage de tous les services',
          );
          // Si pas de profil, afficher tous les services
          this.filteredDailyRecommendations = this.dailyRecommendations;
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('❌ Erreur lors du chargement du profil:', err);
        // En cas d'erreur, afficher tous les services
        this.filteredDailyRecommendations = this.dailyRecommendations;
        this.cdr.markForCheck();
      },
    });
  }

  private generateCalendar() {
    const view = this.calendarView();
    const offset = this.calendarOffset();
    const daysCount = CALENDAR_VIEW_META[view].days;
    const days: CalendarDay[] = [];
    const now = new Date();
    let start: Date;
    if (view === '1w') {
      start = new Date(now);
      start.setDate(now.getDate() + offset * 7);
    } else if (view === '1m') {
      start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    } else if (view === '3m') {
      start = new Date(now);
      start.setMonth(now.getMonth() + offset * 3);
    } else {
      start = new Date(now.getFullYear() + offset, 0, 1);
    }

    for (let i = 0; i < daysCount; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);

      const tasks = Math.floor(Math.random() * 5);
      const completedTasks =
        tasks > 0 ? Math.floor(Math.random() * (tasks + 1)) : 0;

      const activePeriods =
        this.merchantPeriodService.getAllActiveForDate(date);

      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);
      const nowOnly = new Date(now);
      nowOnly.setHours(0, 0, 0, 0);
      const diffDays = Math.round(
        (dateOnly.getTime() - nowOnly.getTime()) / (1000 * 60 * 60 * 24),
      );
      const conditionsList = [
        { icon: '☀️', label: 'Ensoleillé' },
        { icon: '🌤️', label: 'Partiellement nuageux' },
        { icon: '🌧️', label: 'Pluvieux' },
      ];
      const rndCond =
        conditionsList[Math.floor(Math.random() * conditionsList.length)];
      const rndTemp = 12 + Math.floor(Math.random() * 14);
      const weatherData =
        diffDays >= 0 && diffDays < 7
          ? {
              icon: rndCond.icon,
              temperature: rndTemp,
              temperatureMin: rndTemp - 3,
              temperatureMax: rndTemp + 4,
              condition: rndCond.label,
            }
          : undefined;

      days.push({
        date,
        isToday: date.toDateString() === new Date().toDateString(),
        isWeekend: [0, 6].includes(date.getDay()),
        weather: weatherData,
        affluence: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as
          | 'low'
          | 'medium'
          | 'high',
        servicesCount: Math.floor(Math.random() * 3) + 1,
        tasks,
        completedTasks,
        alertsCount: Math.floor(Math.random() * 2),
        specialPeriods: activePeriods.map((p) => ({
          label: p.label,
          type: p.type,
          color: p.color,
        })),
      });
    }

    this.calendarDays = days;

    if (view === '1w') {
      setTimeout(() => {
        if (days.length > 0) {
          this.selectedDay = days[0]; // pre-select without opening modal
          this.updateRecommendationsForDay(days[0]);
          this.cdr.markForCheck();
        }
      }, 0);
    }
  }

  private loadDashboardData(): void {
    this.loading = true;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    forkJoin({
      kpis: this.dashboardService.getKPIs(MerchantType.FIXED),
      predictions: this.dashboardService.getDailyPredictions(tomorrow),
      trends: this.dashboardService.getWeeklyTrends(),
      actions: this.dashboardService.getPendingActions(),
      salesStats: this.dailySalesService.getStats({
        period: 'month',
        merchantType: 'fixed',
      }),
    }).subscribe({
      next: ({ kpis, predictions, trends, actions, salesStats }) => {
        // Mise à jour des données depuis l'API
        this.totalEvents = kpis.totalServices || 0;
        this.avgAccuracy = kpis.averagePrecision || 0;
        this.lossAvoided = kpis.wasteReduction || 0;
        this.totalRevenue = kpis.totalRevenue || 0;
        this.totalSavings = kpis.totalSavings || 0;

        // Stats saisies manuelles du mois
        this.monthlySalesCount = salesStats.overall?.totalSales || 0;
        this.monthlyAverageAccuracy = Math.round(
          salesStats.overall?.averageAccuracy || 0,
        );

        // Construction des stats avec les vraies données
        this.stats = [
          {
            label: 'Économies réalisées',
            value: `${this.totalSavings}€`,
            icon: '💰',
            trend: kpis.savingsGrowth
              ? `${kpis.savingsGrowth > 0 ? '↗ +' : '↘ '}${Math.abs(kpis.savingsGrowth)}% ce mois`
              : 'Stable',
            trendUp: (kpis.savingsGrowth || 0) >= 0,
            class: ' text-yellow-600',
          },
          {
            label: 'Gaspillage évité',
            value: `${this.lossAvoided}€`,
            icon: '📉',
            trend: kpis.wasteReductionDiff
              ? `${kpis.wasteReductionDiff > 0 ? '↗' : '↘'} ${Math.abs(kpis.wasteReductionDiff)}€ économisés`
              : 'Stable',
            trendUp: (kpis.wasteReductionDiff || 0) >= 0,
          },
          {
            label: 'Temps gagné',
            value: `${Math.floor(this.totalEvents * 1.2)}h`,
            icon: '⏱️',
            trend: kpis.servicesDiff
              ? `${kpis.servicesDiff > 0 ? '+' : ''}${Math.floor(kpis.servicesDiff * 1.2)}h ce mois`
              : 'Stable',
            trendUp: (kpis.servicesDiff || 0) >= 0,
          },
          {
            label: 'Amélioration des ventes',
            value: `+${this.avgAccuracy}%`,
            icon: '📈',
            trend: kpis.precisionDiff
              ? `${kpis.precisionDiff > 0 ? '↗ +' : '↘ '}${kpis.precisionDiff}% vs mois dernier`
              : 'Stable',
            trendUp: (kpis.precisionDiff || 0) >= 0,
          },
        ];

        this.weeklyKPIs = {
          precision: kpis.averagePrecision,
          wasteReduction: kpis.wasteReduction,
          revenue: kpis.totalRevenue,
          savings: kpis.totalSavings,
        };

        this.tomorrowPredictions = predictions;
        this.weeklyTrends = trends;
        this.pendingActions = actions;

        this.loading = false;
        this.generateInsightsFromKPIs();
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.log.error('Erreur chargement dashboard fixe:', error);
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Charge les alertes réelles depuis l'API cluster
   */
  private loadMerchantAlerts(): void {
    const user = this.authService.getCurrentUser();
    const userId = (user as any)?._id || (user as any)?.id;
    if (!userId) return;

    this.clusterService.getMerchantAlerts(userId).subscribe({
      next: (res) => {
        this.merchantAlerts = res.alerts || [];
        this.cdr.markForCheck();
      },
      error: () => {
        // Pas de données cluster → alertes vides
        this.merchantAlerts = [];
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Charge les alertes unifiées (météo + événements + calendrier)
   */
  private loadUnifiedAlerts(): void {
    this.authService.getMe().subscribe({
      next: (res) => {
        const loc = (res.user as any)?.location;
        const coords = loc?.coordinates || loc;
        if (!coords?.lat || !coords?.lng) return;
        const city = loc?.city || res.user?.locationCity || '';
        this.alertService
          .loadAlerts(coords.lat, coords.lng, city)
          .subscribe((alerts) => {
            this.unifiedAlerts = alerts;
            this.cdr.markForCheck();
          });
      },
    });
  }

  /**
   * Génère des recommandations positives à partir des KPIs réels
   */
  private generateInsightsFromKPIs(): void {
    this.positiveInsights = [];

    // Précision en amélioration
    if (this.avgAccuracy > 0) {
      const precisionTrend = this.stats.find(
        (s) => s.label === 'Amélioration des ventes',
      );
      if (precisionTrend?.trendUp) {
        this.positiveInsights.push({
          icon: '✅',
          title: 'Précision en hausse',
          message: `Votre précision est à ${this.avgAccuracy}% — ${precisionTrend.trend || 'tendance positive'}`,
          color: 'green',
        });
      }
    }

    // Économies réalisées
    if (this.totalSavings > 0) {
      this.positiveInsights.push({
        icon: '💰',
        title: 'Économies réalisées',
        message: `${this.totalSavings}€ d'économies ce mois grâce à vos prévisions`,
        color: 'green',
      });
    }

    // Gaspillage évité
    if (this.lossAvoided > 0) {
      this.positiveInsights.push({
        icon: '📉',
        title: 'Gaspillage réduit',
        message: `${this.lossAvoided}€ de gaspillage évité — continuez sur cette lancée`,
        color: 'purple',
      });
    }

    // Saisies du mois
    if (this.monthlySalesCount >= 10) {
      this.positiveInsights.push({
        icon: '📈',
        title: 'Données solides',
        message: `${this.monthlySalesCount} saisies ce mois — vos prédictions s'améliorent`,
        color: 'blue',
      });
    } else if (this.monthlySalesCount > 0 && this.monthlySalesCount < 10) {
      this.positiveInsights.push({
        icon: '📊',
        title: 'Continuez vos saisies',
        message: `${this.monthlySalesCount}/10 saisies minimum ce mois — plus de données = meilleures prédictions`,
        color: 'blue',
      });
    }

    this.cdr.markForCheck();
  }

  selectDay(day: CalendarDay) {
    this.selectedDay = day;

    // Mettre à jour le label selon le jour
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(day.date);
    selectedDate.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (selectedDate.getTime() === today.getTime()) {
      this.selectedDayLabel = 'Recommandations du jour';
    } else if (selectedDate.getTime() === tomorrow.getTime()) {
      this.selectedDayLabel = 'Recommandations pour demain';
    } else {
      const dayName = selectedDate.toLocaleDateString('fr-FR', {
        weekday: 'long',
      });
      const dateStr = selectedDate.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
      });
      this.selectedDayLabel = `Recommandations du ${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dateStr}`;
    }

    // Mettre à jour les infos du jour sélectionné
    this.selectedDayInfo = {
      dayName: selectedDate.toLocaleDateString('fr-FR', { weekday: 'short' }),
      dayNumber: selectedDate.getDate(),
      month: selectedDate.toLocaleDateString('fr-FR', { month: 'short' }),
      weather: day.weather ?? { icon: '❓', temperature: 0 },
      servicesCount: day.servicesCount,
      tasks: day.tasks,
      completedTasks: day.completedTasks,
      isWeekend: day.isWeekend,
      affluence: day.affluence || 'moyenne',
    };

    // Générer des recommandations pour ce jour (mock)
    this.updateRecommendationsForDay(day);
    // Ouvrir le modal détail jour (sauf si la vue 1w sélectionne automatiquement le 1er jour au load)
    this.showCalendarDayModal = true;
    this.cdr.markForCheck();
  }

  showCalendarDayModal = false;

  closeCalendarDayModal(): void {
    this.showCalendarDayModal = false;
    this.cdr.markForCheck();
  }

  toggleRecommendations() {
    this.showRecommendations = !this.showRecommendations;
  }

  toggleServiceExpansion(index: number) {
    this.expandedServices[index] = !this.expandedServices[index];
  }

  isServiceExpanded(index: number): boolean {
    return this.expandedServices[index] || false;
  }

  toggleExplanationDetails() {
    this.showExplanationDetails = !this.showExplanationDetails;
  }

  toggleDetailedComparison() {
    this.showDetailedComparison = !this.showDetailedComparison;
  }

  // Calcul cohérent des impacts par service
  getServiceTotalLoss(products: any[]): number {
    // Coût moyen d'un produit gaspillé : 1.5€
    // 2/3 des produits = sur-préparation de 30%
    const wasteProducts = Math.ceil((products.length * 2) / 3);
    const avgWastePerProduct =
      products
        .slice(0, wasteProducts)
        .reduce((sum, p) => sum + Math.round(p.recommended * 0.3) * 1.5, 0) /
      wasteProducts;

    // Prix moyen de vente d'un produit manqué : 3€
    // 1/3 des produits = sous-préparation de 25% (ventes manquées)
    const missedSalesProducts = Math.floor(products.length / 3);
    const avgMissedSalesPerProduct =
      products
        .slice(wasteProducts)
        .reduce((sum, p) => sum + Math.round(p.recommended * 0.25) * 3, 0) /
      (missedSalesProducts || 1);

    return Math.round(
      wasteProducts * avgWastePerProduct +
        missedSalesProducts * avgMissedSalesPerProduct,
    );
  }

  getServiceTotalGain(products: any[]): number {
    // Gain = 100% de la perte évitée (complémentaire)
    return this.getServiceTotalLoss(products);
  }

  getProductSavings(productIndex: number, recommended: number): number {
    // 1 produit sur 3 : ventes supplémentaires
    if (productIndex % 3 === 0) {
      return Math.round(recommended * 0.25 * 3); // Quantité * prix moyen 3€
    }
    // 2 produits sur 3 : gaspillage évité
    return Math.round(recommended * 0.3 * 1.5); // Quantité * coût moyen 1.5€
  }

  // Totaux globaux pour la section AVANT/APRÈS
  getTotalLossAllServices(): number {
    return this.filteredDailyRecommendations.reduce(
      (total, service) => total + this.getServiceTotalLoss(service.products),
      0,
    );
  }

  getTotalGainAllServices(): number {
    return this.filteredDailyRecommendations.reduce(
      (total, service) => total + this.getServiceTotalGain(service.products),
      0,
    );
  }

  getWasteOnlyLoss(): number {
    // Calcul du gaspillage uniquement (2/3 des produits)
    const total = this.filteredDailyRecommendations.reduce((total, service) => {
      const wasteProducts = Math.ceil((service.products.length * 2) / 3);
      const wasteCost = service.products
        .slice(0, wasteProducts)
        .reduce(
          (sum: number, p: any) => sum + Math.round(p.recommended * 0.3 * 1.5),
          0,
        );
      return total + wasteCost;
    }, 0);
    return Math.round(total);
  }

  getMissedSalesLoss(): number {
    // Calcul des ventes manquées uniquement (1/3 des produits)
    const total = this.filteredDailyRecommendations.reduce((total, service) => {
      const missedProducts = Math.floor(service.products.length / 3);
      const wasteProducts = Math.ceil((service.products.length * 2) / 3);
      const missedSalesCost = service.products
        .slice(wasteProducts)
        .reduce(
          (sum: number, p: any) => sum + Math.round(p.recommended * 0.25 * 3),
          0,
        );
      return total + missedSalesCost;
    }, 0);
    return Math.round(total);
  }

  getWasteOnlyGain(): number {
    return this.getWasteOnlyLoss(); // 100% du gaspillage évité
  }

  getMissedSalesGain(): number {
    return this.getMissedSalesLoss(); // 100% des ventes récupérées
  }

  // Chiffre d'affaires estimé de base (pour contextualiser)
  getBaseRevenue(): number {
    // CA moyen par produit * nombre total de produits recommandés
    const total = this.filteredDailyRecommendations.reduce((total, service) => {
      const serviceRevenue = service.products.reduce(
        (sum: number, p: any) => sum + p.recommended * 3,
        0, // Prix moyen de vente : 3€
      );
      return total + serviceRevenue;
    }, 0);
    return Math.round(total);
  }

  toggleTask(taskId: number) {
    const task = this.preparationTasks.find((t) => t.id === taskId);
    if (task) {
      task.completed = !task.completed;
      // Mettre à jour le compteur dans selectedDayInfo
      const completed = this.preparationTasks.filter((t) => t.completed).length;
      this.selectedDayInfo.completedTasks = completed;
      this.selectedDayInfo.tasks = this.preparationTasks.length;
    }
  }

  openChecklist() {
    if (!this.planFeatureService.canAccess(FEATURES.PREPARATION_CHECKLIST))
      return;
    this.showChecklist = true;
    this.isChecklistClosing = false;
    this.cdr.markForCheck();
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est mis à jour
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isChecklistAnimating = true;
        this.cdr.markForCheck();
      });
    });
  }

  closeChecklist() {
    this.isChecklistClosing = true;
    this.isChecklistAnimating = false;
    setTimeout(() => {
      this.showChecklist = false;
      this.isChecklistClosing = false;
      this.cdr.markForCheck();
    }, 300); // Durée de l'animation
  }

  getCompletedTasksCount(): number {
    return this.preparationTasks.filter((t) => t.completed).length;
  }

  getTasksByCategory(category: string): number {
    return this.preparationTasks.filter((t) => t.category === category).length;
  }

  getCompletedTasksByCategory(category: string): number {
    return this.preparationTasks.filter(
      (t) => t.category === category && t.completed,
    ).length;
  }

  isCategoryComplete(category: string): boolean {
    const tasksInCategory = this.preparationTasks.filter(
      (t) => t.category === category,
    );
    if (tasksInCategory.length === 0) return false;
    return tasksInCategory.every((t) => t.completed);
  }

  hasUrgentIncompleteTask(category: string): boolean {
    return this.preparationTasks.some(
      (t) => t.category === category && t.priority === 'high' && !t.completed,
    );
  }

  updateRecommendationsForDay(day: CalendarDay) {
    // Simuler des variations de recommandations selon le jour
    const isWeekend = day.isWeekend;
    const factor = isWeekend ? 1.3 : 1.0; // +30% le weekend

    // Créer un nouveau tableau pour forcer la détection de changement par Angular
    this.dailyRecommendations = [
      {
        service: 'Service du midi',
        timeSlot: '11h30 - 14h00',
        icon: '🍽️',
        status: 'active',
        products: [
          {
            name: 'Burger Classique',
            icon: '🍔',
            recommended: Math.round(45 * factor),
            range: `${Math.round(35 * factor)}-${Math.round(55 * factor)}`,
            confidence: 92,
          },
          {
            name: 'Burger Bacon',
            icon: '🍔',
            recommended: Math.round(38 * factor),
            range: `${Math.round(30 * factor)}-${Math.round(46 * factor)}`,
            confidence: 90,
          },
          {
            name: 'Burger Végétarien',
            icon: '🥬',
            recommended: Math.round(22 * factor),
            range: `${Math.round(18 * factor)}-${Math.round(26 * factor)}`,
            confidence: 86,
          },
          {
            name: 'Wrap Poulet',
            icon: '🌯',
            recommended: Math.round(35 * factor),
            range: `${Math.round(28 * factor)}-${Math.round(42 * factor)}`,
            confidence: 89,
          },
          {
            name: 'Salade César',
            icon: '🥗',
            recommended: Math.round(28 * factor),
            range: `${Math.round(22 * factor)}-${Math.round(34 * factor)}`,
            confidence: 88,
          },
          {
            name: 'Salade Composée',
            icon: '🥗',
            recommended: Math.round(24 * factor),
            range: `${Math.round(19 * factor)}-${Math.round(29 * factor)}`,
            confidence: 85,
          },
          {
            name: 'Frites',
            icon: '🍟',
            recommended: Math.round(60 * factor),
            range: `${Math.round(50 * factor)}-${Math.round(70 * factor)}`,
            confidence: 90,
          },
          {
            name: 'Onion Rings',
            icon: '🧅',
            recommended: Math.round(32 * factor),
            range: `${Math.round(26 * factor)}-${Math.round(38 * factor)}`,
            confidence: 84,
          },
          {
            name: 'Nuggets',
            icon: '🍗',
            recommended: Math.round(40 * factor),
            range: `${Math.round(32 * factor)}-${Math.round(48 * factor)}`,
            confidence: 87,
          },
          {
            name: 'Coca-Cola',
            icon: '🥤',
            recommended: Math.round(55 * factor),
            range: `${Math.round(45 * factor)}-${Math.round(65 * factor)}`,
            confidence: 85,
          },
          {
            name: 'Sprite',
            icon: '🥤',
            recommended: Math.round(42 * factor),
            range: `${Math.round(35 * factor)}-${Math.round(50 * factor)}`,
            confidence: 83,
          },
          {
            name: 'Eau minérale',
            icon: '💧',
            recommended: Math.round(48 * factor),
            range: `${Math.round(40 * factor)}-${Math.round(56 * factor)}`,
            confidence: 88,
          },
          {
            name: 'Café',
            icon: '☕',
            recommended: Math.round(65 * factor),
            range: `${Math.round(55 * factor)}-${Math.round(75 * factor)}`,
            confidence: 91,
          },
          {
            name: 'Thé glacé',
            icon: '🧋',
            recommended: Math.round(28 * factor),
            range: `${Math.round(22 * factor)}-${Math.round(34 * factor)}`,
            confidence: 82,
          },
          {
            name: 'Milkshake',
            icon: '🥤',
            recommended: Math.round(18 * factor),
            range: `${Math.round(14 * factor)}-${Math.round(22 * factor)}`,
            confidence: 80,
          },
        ],
      },
      {
        service: 'Service du soir',
        timeSlot: '18h30 - 22h00',
        icon: '🌙',
        status: 'active',
        products: [
          {
            name: 'Pizza Margherita',
            icon: '🍕',
            recommended: Math.round(32 * factor),
            range: `${Math.round(28 * factor)}-${Math.round(38 * factor)}`,
            confidence: 89,
          },
          {
            name: 'Pizza 4 Fromages',
            icon: '🍕',
            recommended: Math.round(28 * factor),
            range: `${Math.round(24 * factor)}-${Math.round(34 * factor)}`,
            confidence: 87,
          },
          {
            name: 'Pizza Regina',
            icon: '🍕',
            recommended: Math.round(26 * factor),
            range: `${Math.round(22 * factor)}-${Math.round(32 * factor)}`,
            confidence: 86,
          },
          {
            name: 'Pâtes Carbonara',
            icon: '🍝',
            recommended: Math.round(25 * factor),
            range: `${Math.round(20 * factor)}-${Math.round(30 * factor)}`,
            confidence: 87,
          },
          {
            name: 'Pâtes Bolognaise',
            icon: '🍝',
            recommended: Math.round(30 * factor),
            range: `${Math.round(25 * factor)}-${Math.round(36 * factor)}`,
            confidence: 88,
          },
          {
            name: 'Lasagnes',
            icon: '🍝',
            recommended: Math.round(22 * factor),
            range: `${Math.round(18 * factor)}-${Math.round(26 * factor)}`,
            confidence: 85,
          },
          {
            name: 'Risotto',
            icon: '🍚',
            recommended: Math.round(18 * factor),
            range: `${Math.round(14 * factor)}-${Math.round(22 * factor)}`,
            confidence: 83,
          },
          {
            name: 'Steak Frites',
            icon: '🥩',
            recommended: Math.round(24 * factor),
            range: `${Math.round(20 * factor)}-${Math.round(28 * factor)}`,
            confidence: 86,
          },
          {
            name: 'Poulet Rôti',
            icon: '🍗',
            recommended: Math.round(20 * factor),
            range: `${Math.round(16 * factor)}-${Math.round(24 * factor)}`,
            confidence: 84,
          },
          {
            name: 'Tiramisu',
            icon: '🍰',
            recommended: Math.round(18 * factor),
            range: `${Math.round(15 * factor)}-${Math.round(22 * factor)}`,
            confidence: 84,
          },
          {
            name: 'Panna Cotta',
            icon: '🍮',
            recommended: Math.round(15 * factor),
            range: `${Math.round(12 * factor)}-${Math.round(18 * factor)}`,
            confidence: 82,
          },
          {
            name: 'Tarte Citron',
            icon: '🍋',
            recommended: Math.round(12 * factor),
            range: `${Math.round(10 * factor)}-${Math.round(16 * factor)}`,
            confidence: 80,
          },
          {
            name: 'Vin rouge',
            icon: '🍷',
            recommended: Math.round(12 * factor),
            range: `${Math.round(10 * factor)}-${Math.round(15 * factor)}`,
            confidence: 82,
          },
          {
            name: 'Vin blanc',
            icon: '🍷',
            recommended: Math.round(10 * factor),
            range: `${Math.round(8 * factor)}-${Math.round(13 * factor)}`,
            confidence: 81,
          },
          {
            name: 'Bière pression',
            icon: '🍺',
            recommended: Math.round(28 * factor),
            range: `${Math.round(24 * factor)}-${Math.round(34 * factor)}`,
            confidence: 85,
          },
        ],
      },
    ];
  }

  downloadPreparationSheet() {
    this.log.debug('Téléchargement fiche préparation');
  }

  navigateToSales(date: Date) {
    this.log.debug('Saisir ventes pour', date);
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  /**
   * Pour la vue multi-semaine : retourne un tableau de N cases vides
   * correspondant au décalage du premier jour (lundi = 0).
   */
  getLeadingBlanks(): number[] {
    if (!this.calendarDays.length) return [];
    const first = this.calendarDays[0].date;
    // getDay() retourne 0=dimanche...6=samedi ; on veut lundi=0
    const dow = (first.getDay() + 6) % 7;
    return Array(dow).fill(0);
  }

  // Export PDF - Service spécifique
  exportServicePDF(service: any) {
    const doc = new jsPDF();

    // En-tête avec couleur agrandi
    doc.setFillColor(147, 51, 234); // Purple
    doc.rect(0, 0, 210, 45, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text('RECOMMANDATIONS', 105, 12, { align: 'center' });

    doc.setFontSize(14);
    doc.text(`${service.service}`, 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`${service.timeSlot}`, 105, 27, { align: 'center' });

    // Informations du jour dans le header
    const today = new Date();
    doc.setFontSize(8);
    doc.text(
      `Date: ${today.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`,
      105,
      34,
      { align: 'center' },
    );
    doc.text(
      `M\u00E9t\u00E9o: ${this.selectedDayInfo.weather.temperature}\u00B0C | Affluence: ${this.selectedDayInfo.affluence}`,
      105,
      40,
      { align: 'center' },
    );

    // Titre section
    doc.setTextColor(88, 28, 135);
    doc.setFontSize(11);
    doc.text('QUANTIT\u00C9S RECOMMAND\u00C9ES', 20, 54);

    // Fond pour les en-têtes de colonnes
    doc.setFillColor(237, 233, 254);
    doc.rect(20, 58, 170, 8, 'F');

    // En-tête de colonnes
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('PRODUIT', 25, 64);
    doc.text('QUANTITE', 145, 64);
    doc.text('PLAGE', 175, 64);

    doc.setFont('helvetica', 'normal');

    // Liste des produits
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    let yPos = 72;

    service.products.forEach((p: any, index: number) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }

      // Nom du produit
      doc.setFont('helvetica', 'bold');
      doc.text(`${p.name}`, 25, yPos);

      // Quantité recommandée (aligné à droite)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(`${p.recommended} unités`, 165, yPos);
      doc.setFont('helvetica', 'normal');

      // Plage de confiance
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(7);
      doc.text(`(${p.range})`, 165, yPos + 3);

      // Comparaison SANS / AVEC
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(6);
      if (index % 3 === 0) {
        // Cas rupture stock
        doc.setTextColor(234, 88, 12);
        doc.text(
          `Sans outil: ${Math.round(p.recommended * 0.75)} pr\u00E9par\u00E9s (ruptures) => Predivent: +${Math.round(p.recommended * 0.25)} ventes capt\u00E9es`,
          30,
          yPos + 3,
        );
      } else {
        // Cas sur-préparation
        doc.setTextColor(22, 163, 74);
        doc.text(
          `Sans outil: ${Math.round(p.recommended * 1.3)} pr\u00E9par\u00E9s (${Math.round(p.recommended * 0.3)} jet\u00E9s) => Predivent: z\u00E9ro gaspillage`,
          30,
          yPos + 3,
        );
      }
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);

      yPos += 9;
    });

    // Résumé financier en bas
    yPos += 10;
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Pied de page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Généré par Predivent le ${new Date().toLocaleDateString('fr-FR')}`,
      105,
      285,
      { align: 'center' },
    );

    // Téléchargement
    doc.save(
      `recommandations-${service.service.toLowerCase().replace(/\s+/g, '-')}.pdf`,
    );
  }

  // Export PDF - Fiche complète (tous les services)
  exportCompletePDF() {
    const doc = new jsPDF();

    // En-tête avec dégradé
    doc.setFillColor(147, 51, 234);
    doc.rect(0, 0, 210, 50, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text('FICHE COMPLÈTE', 105, 18, { align: 'center' });

    doc.setFontSize(14);
    doc.text('RECOMMANDATIONS DU JOUR', 105, 28, { align: 'center' });

    const today = new Date();
    doc.setFontSize(11);
    doc.text(
      `${today.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`,
      105,
      38,
      { align: 'center' },
    );
    doc.text(
      `Météo: ${this.selectedDayInfo.weather.temperature}°C | Affluence: ${this.selectedDayInfo.affluence}`,
      105,
      45,
      { align: 'center' },
    );

    // Résumé financier
    doc.setFillColor(34, 197, 94);
    doc.roundedRect(20, 58, 170, 40, 3, 3, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text('RÉSUMÉ FINANCIER', 105, 68, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`CA base: ${this.getBaseRevenue()}€`, 30, 78);
    doc.text(
      `CA sans outil: ${this.getBaseRevenue() - this.getWasteOnlyLoss()}€`,
      30,
      86,
    );
    doc.text(
      `CA avec Predivent: ${this.getBaseRevenue() + this.getMissedSalesLoss()}€`,
      30,
      94,
    );
    doc.text(
      `Économies totales: +${this.getWasteOnlyLoss() + this.getMissedSalesLoss()}€`,
      120,
      78,
    );
    doc.text(`Gaspillage évité: ${this.getWasteOnlyLoss()}€`, 120, 86);
    doc.text(`Ventes supplémentaires: ${this.getMissedSalesLoss()}€`, 120, 94);

    let yPos = 118;

    // Services
    this.dailyRecommendations.forEach((service, serviceIndex) => {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }

      // Titre du service
      doc.setFillColor(237, 233, 254);
      doc.rect(20, yPos - 5, 170, 12, 'F');

      doc.setTextColor(88, 28, 135);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(`${service.service}`, 25, yPos + 2);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`${service.timeSlot}`, 150, yPos + 2);

      yPos += 10;

      // En-têtes de colonnes pour les produits
      doc.setFillColor(245, 245, 245);
      doc.rect(20, yPos - 3, 170, 8, 'F');

      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('PRODUIT', 25, yPos + 2);
      doc.text('QUANTITE', 150, yPos + 2);
      doc.text('PLAGE', 175, yPos + 2);
      doc.setFont('helvetica', 'normal');

      yPos += 8;

      // Produits du service
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);

      service.products.forEach((p: any, pi: number) => {
        if (yPos > 265) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(8);
        doc.text(`${p.name}`, 25, yPos);
        doc.setFont('helvetica', 'bold');
        doc.text(`${p.recommended}`, 165, yPos);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(128, 128, 128);
        doc.setFontSize(7);
        doc.text(`(${p.range})`, 173, yPos);

        // Comparaison SANS / AVEC
        doc.setFontSize(6);
        if (pi % 3 === 0) {
          doc.setTextColor(234, 88, 12);
          doc.text(
            `Sans outil: ${Math.round(p.recommended * 0.75)} pr\u00E9par\u00E9s (ruptures) => Predivent: +${Math.round(p.recommended * 0.25)} ventes capt\u00E9es`,
            30,
            yPos + 3,
          );
        } else {
          doc.setTextColor(22, 163, 74);
          doc.text(
            `Sans outil: ${Math.round(p.recommended * 1.3)} pr\u00E9par\u00E9s (${Math.round(p.recommended * 0.3)} jet\u00E9s) => Predivent: z\u00E9ro gaspillage`,
            30,
            yPos + 3,
          );
        }
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);

        yPos += 8;
      });

      yPos += 8;
    });

    // Pied de page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Généré par Predivent - Optimisation IA | ${new Date().toLocaleDateString('fr-FR')}`,
      105,
      285,
      { align: 'center' },
    );

    doc.save(`fiche-complete-${new Date().toISOString().split('T')[0]}.pdf`);
  }

  // Export PDF - Récapitulatif global (section AVANT/AVEC)
  exportSummaryPDF() {
    const doc = new jsPDF();

    // En-tête avec gradient simulé
    doc.setFillColor(147, 51, 234);
    doc.rect(0, 0, 210, 45, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('RÉCAPITULATIF', 105, 20, { align: 'center' });

    doc.setFontSize(14);
    doc.text('IMPACT PREDIVENT', 105, 32, { align: 'center' });

    const today = new Date();
    doc.setFontSize(10);
    doc.text(
      `${today.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`,
      105,
      40,
      { align: 'center' },
    );

    let yPos = 60;

    // Section SANS RECOMMANDATIONS
    doc.setFillColor(254, 226, 226); // Red light
    doc.roundedRect(20, yPos, 80, 65, 3, 3, 'F');

    doc.setTextColor(185, 28, 28);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('SANS', 60, yPos + 10, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    doc.text(`CA potentiel:`, 25, yPos + 20);
    doc.text(`${this.getBaseRevenue()}€`, 90, yPos + 20, { align: 'right' });

    doc.setTextColor(185, 28, 28);
    doc.text(`Gaspillage (perte):`, 25, yPos + 28);
    doc.text(`-${this.getWasteOnlyLoss()}€`, 90, yPos + 28, { align: 'right' });

    doc.setDrawColor(185, 28, 28);
    doc.setLineWidth(0.5);
    doc.line(25, yPos + 32, 95, yPos + 32);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`CA effectif:`, 25, yPos + 40);
    doc.text(
      `${this.getBaseRevenue() - this.getWasteOnlyLoss()}€`,
      90,
      yPos + 40,
      { align: 'right' },
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(234, 88, 12);
    doc.text(`Ventes manquées: -${this.getMissedSalesLoss()}€`, 25, yPos + 48);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Temps: 2h/jour`, 60, yPos + 58, { align: 'center' });

    // Section AVEC PREDIVENT
    doc.setFillColor(220, 252, 231); // Green light
    doc.roundedRect(110, yPos, 80, 65, 3, 3, 'F');

    doc.setTextColor(22, 163, 74);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('AVEC', 150, yPos + 10, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    doc.text(`CA potentiel:`, 115, yPos + 20);
    doc.text(`${this.getBaseRevenue()}€`, 180, yPos + 20, { align: 'right' });

    doc.setTextColor(156, 163, 175);
    doc.text(`Gaspillage:`, 115, yPos + 28);
    doc.text(`0€`, 180, yPos + 28, { align: 'right' });

    doc.setTextColor(16, 185, 129);
    doc.text(`Ventes supplémentaires:`, 115, yPos + 36);
    doc.text(`+${this.getMissedSalesLoss()}€`, 180, yPos + 36, {
      align: 'right',
    });

    doc.setDrawColor(22, 163, 74);
    doc.setLineWidth(0.5);
    doc.line(115, yPos + 40, 185, yPos + 40);

    doc.setTextColor(22, 163, 74);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`CA effectif:`, 115, yPos + 48);
    doc.text(
      `${this.getBaseRevenue() + this.getMissedSalesLoss()}€`,
      180,
      yPos + 48,
      { align: 'right' },
    );

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Temps: 2h gagnées`, 150, yPos + 58, { align: 'center' });

    // Économies totales
    yPos += 80;
    doc.setFillColor(239, 68, 68);
    doc.setDrawColor(239, 68, 68);
    doc.setLineWidth(2);
    doc.roundedRect(40, yPos, 130, 30, 3, 3, 'FD');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text('ÉCONOMIES TOTALES', 105, yPos + 10, { align: 'center' });

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `+${this.getWasteOnlyLoss() + this.getMissedSalesLoss()}€`,
      105,
      yPos + 22,
      { align: 'center' },
    );

    // Pied de page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Généré par Predivent le ${new Date().toLocaleDateString('fr-FR')}`,
      105,
      285,
      { align: 'center' },
    );

    doc.save(`recapitulatif-${new Date().toISOString().split('T')[0]}.pdf`);
  }
}
